import {
  Button,
  Center,
  chakra,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import ErrorBox from "../../../shared/components/ErrorBox";
import useApi from "../../../shared/hooks/useApi";
import { PatientId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import { ChangeRequestResolveModal } from "./ChangeRequestResolveModal";
import ChangeRequestStatusTag from "./ChangeRequestStatusTag";

export default function PatientTaskDocumentChangeRequestsTable(props: {
  patientId: PatientId;
  onResolve: () => void;
}) {
  const { queries } = useApi();
  const query = useQuery(queries.patient.taskDocumentsChangeRequests(props.patientId));

  return (
    <Flex direction="column" gap="5">
      <Text fontSize="lg" fontWeight="semibold">
        Document Change Requests
      </Text>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Request</Th>
              <Th>Status</Th>
              <Th>Created At</Th>
              <Th>Response</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(() => {
              switch (query.status) {
                case "pending":
                  return (
                    <Tr>
                      <Td colSpan={4}>
                        <Center>
                          <Spinner size="sm" />
                        </Center>
                      </Td>
                    </Tr>
                  );
                case "error":
                  return (
                    <Tr>
                      <Td colSpan={4}>
                        <ErrorBox error={query.error} resetErrorBoundary={query.refetch} />
                      </Td>
                    </Tr>
                  );
                case "success":
                  return query.data.length === 0 ? (
                    <Tr>
                      <Td colSpan={4}>
                        <NoResults>No change requests</NoResults>
                      </Td>
                    </Tr>
                  ) : (
                    query.data.map((req) => (
                      <Tr key={req.id}>
                        <Td>{req.request}</Td>
                        <Td>
                          <ChangeRequestStatusTag status={req.status} />
                        </Td>
                        <Td>{dateFormatter.toDateTime(req.createdAt)}</Td>
                        <Td>
                          {req.status === "resolved" ? (
                            req.response
                          ) : (
                            <ChangeRequestResolveModal
                              changeRequestId={req.id}
                              patientId={props.patientId}
                              onResolve={() => {
                                query.refetch();
                                props.onResolve();
                              }}
                            >
                              <Button size="sm">Resolve</Button>
                            </ChangeRequestResolveModal>
                          )}
                        </Td>
                      </Tr>
                    ))
                  );
              }
            })()}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
}

const NoResults = chakra("div", {
  base: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "full",
    bg: "bg.subtle",
    color: "fg.muted",
    rounded: "l2",
    py: "4",
  },
});
